/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// In production, we register a service worker to serve assets from local cache.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on the "N+1" visit to a page, since previously
// cached resources are updated in the background.

// To learn more about the benefits of this model, read https://goo.gl/KwvDNy.
// This link also includes instructions on opting out of this behavior.

const isLocalhost = Boolean(
    window.location.hostname === 'localhost'
    // [::1] is the IPv6 localhost address.
    || window.location.hostname === '[::1]'
    // 127.0.0.1/8 is considered localhost for IPv4.
    || window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
);

const SERVICE_WORKER_VERSION_STORAGE_KEY = 'mbp-service-worker-version';

function getServiceWorkerVersion() {
    try {
        const persistedApp = JSON.parse(window.sessionStorage.getItem('persist:app') || '{}');
        const featureFlags = JSON.parse(persistedApp?.featureFlags || '{}');

        return featureFlags?.['app-sw-version'] || window?.mbpSwVersionNumber || '0.0.0';
    } catch (error) {
        console.error('Error reading service worker version:', error);
        return window?.mbpSwVersionNumber || '0.0.0';
    }
}

function getStoredServiceWorkerVersion() {
    try {
        return window.localStorage.getItem(SERVICE_WORKER_VERSION_STORAGE_KEY);
    } catch (error) {
        console.error('Error reading stored service worker version:', error);
        return null;
    }
}

function setStoredServiceWorkerVersion(version) {
    try {
        window.localStorage.setItem(SERVICE_WORKER_VERSION_STORAGE_KEY, version);
    } catch (error) {
        console.error('Error storing service worker version:', error);
    }
}

function shouldForceRefreshServiceWorker() {
    const SERVICE_WORKER_VERSION = getServiceWorkerVersion();
    if (!SERVICE_WORKER_VERSION || SERVICE_WORKER_VERSION === '0.0.0') {
        return false;
    }

    return getStoredServiceWorkerVersion() !== SERVICE_WORKER_VERSION;
}

async function removeExistingServiceWorkers() {
    const registrations = await navigator.serviceWorker.getRegistrations();

    await Promise.all(registrations.map((registration) => registration.unregister()));

    if ('caches' in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
    }
}

function registerValidSW(swUrl) {
    console.log('registerServiceWorker | registerValidSW ', swUrl);
    return navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
            const reg = registration;

            reg.onupdatefound = () => {
                const installingWorker = registration.installing;
                installingWorker.onstatechange = () => {
                    if (installingWorker.state === 'installed') {
                        if (navigator.serviceWorker.controller) {
                            // At this point, the old content will have been purged and
                            // the fresh content will have been added to the cache.
                            // It's the perfect time to display a "New content is
                            // available; please refresh." message in your web app.
                            console.log('New content is available; please refresh.');
                        } else {
                            // At this point, everything has been precached.
                            // It's the perfect time to display a
                            // "Content is cached for offline use." message.
                            console.log('Content is cached for offline use.');
                        }
                    }
                };
            };

            // Remove existing SWs (TODO - REMOVE ME)
            // reg.unregister().then(boolean => console.log('SW unregister is successful', boolean));

            return registration;
        })
        .catch((error) => {
            console.error('Error during service worker registration:', error);
            throw error;
        });
}

function checkValidServiceWorker(swUrl) {
    console.log('registerServiceWorker | checkValidServiceWorker  ', swUrl);
    // Check if the service worker can be found. If it can't reload the page.
    return fetch(swUrl)
        .then((response) => {
            // Ensure service worker exists, and that we really are getting a JS file.
            const contentType = response.headers.get('content-type') || '';
            if (
                response.status === 404
        || contentType.indexOf('javascript') === -1
            ) {
                console.log('No service worker found. Probably a different app. Reload the page.');
                // No service worker found. Probably a different app. Reload the page.
                navigator.serviceWorker.ready.then((registration) => {
                    registration.unregister().then(() => {
                        window.location.reload();
                    });
                });
                return null;
            }
            console.log('Service worker found. Proceed as normal.');
            // Service worker found. Proceed as normal.
            return registerValidSW(swUrl);
        })
        .catch((ex) => {
            console.log('No internet connection found. App is running in offline mode.', ex);
            throw ex;
        });
}

export default function register() {
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
        console.log('registerServiceWorker | register');
        // The URL constructor is available in all browsers that support SW.
        const publicUrl = new URL(process.env.PUBLIC_URL, window.location);

        if (publicUrl.origin !== window.location.origin) {
            // Our service worker won't work if PUBLIC_URL is on a different origin
            // from what our page is served on. This might happen if a CDN is used to
            // serve assets; see https://github.com/facebookincubator/create-react-app/issues/2374
            return;
        }

        window.addEventListener('load', () => {
            const swUrl = '/service-worker.js';
            const startRegistration = () => {
                if (isLocalhost) {
                    // This is running on localhost. Lets check if a service worker still exists or not.
                    const registration = checkValidServiceWorker(swUrl);

                    // Add some additional logging to localhost, pointing developers to the
                    // service worker/PWA documentation.
                    navigator.serviceWorker.ready.then(() => {
                        console.log(
                            'This web app is being served cache-first by a service '
                  + 'worker. To learn more, visit https://goo.gl/SC7cgQ',
                        );
                    });

                    return registration;
                }
                // Is not local host. Just register service worker
                return registerValidSW(swUrl);
            };

            const registrationFlow = shouldForceRefreshServiceWorker()
                ? removeExistingServiceWorkers()
                    .then(() => {
                        const SERVICE_WORKER_VERSION = getServiceWorkerVersion();
                        console.log('Existing service workers and caches removed. New version is', SERVICE_WORKER_VERSION);
                        return startRegistration();
                    })
                    .catch((error) => {
                        console.error('Error removing existing service workers:', error);
                        return startRegistration();
                    })
                : startRegistration();

            registrationFlow
                .then((registration) => {
                    const SERVICE_WORKER_VERSION = getServiceWorkerVersion();
                    if (shouldForceRefreshServiceWorker() && SERVICE_WORKER_VERSION && SERVICE_WORKER_VERSION !== '0.0.0') {
                        setStoredServiceWorkerVersion(SERVICE_WORKER_VERSION);
                    }
                })
                .catch((error) => {
                    console.error('Service worker registration flow failed:', error);
                });
        });
    }
}

export function unregister() {
    if ('serviceWorker' in navigator) {
        removeExistingServiceWorkers().catch((error) => {
            console.error('Error removing existing service workers:', error);
        });
    }
}
